<template>
  <ContentWrap>
    <el-alert
      type="info"
      :closable="false"
      show-icon
      class="mb-12px"
      title="本所顾问协议：绑定本所客户；可选关联 Matter。可记服务记录、终止协议。"
    />
    <el-form :inline="true" class="mb-12px" @submit.prevent>
      <el-form-item label="状态">
        <el-select v-model="query.status" clearable class="!w-120px">
          <el-option label="生效中" value="ACTIVE" />
          <el-option label="已到期" value="EXPIRED" />
          <el-option label="已终止" value="TERMINATED" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleQuery">搜索</el-button>
        <el-button type="success" @click="openCreate">新建协议</el-button>
      </el-form-item>
    </el-form>
    <el-table v-loading="loading" :data="list" border>
      <el-table-column prop="id" label="编号" width="70" />
      <el-table-column prop="title" label="标题" min-width="140" show-overflow-tooltip />
      <el-table-column prop="customerName" label="客户" min-width="120" show-overflow-tooltip />
      <el-table-column prop="annualFee" label="年费" width="100" />
      <el-table-column prop="startDate" label="开始" width="110" />
      <el-table-column prop="endDate" label="结束" width="110" />
      <el-table-column prop="status" label="状态" width="100" />
      <el-table-column label="操作" width="180" fixed="right">
        <template #default="{ row }">
          <el-button link type="success" @click="openLogs(row)">服务记录</el-button>
          <el-button v-if="row.status === 'ACTIVE'" link type="danger" @click="doTerminate(row)">
            终止
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="getList"
    />

    <Dialog v-model="createVisible" title="新建顾问协议" width="520px">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="90px">
        <el-form-item label="客户" prop="customerId">
          <el-select v-model="form.customerId" filterable class="!w-100%">
            <el-option
              v-for="c in customers"
              :key="c.id"
              :label="`${c.name}（${c.customerNo || c.id}）`"
              :value="c.id!"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="关联案件">
          <el-select v-model="form.caseId" clearable filterable class="!w-100%" placeholder="可选">
            <el-option
              v-for="m in matters"
              :key="m.id"
              :label="`${m.firmCaseNo || m.id} · ${m.title}`"
              :value="m.id!"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="标题" prop="title"><el-input v-model="form.title" /></el-form-item>
        <el-form-item label="年费" prop="annualFee">
          <el-input-number v-model="form.annualFee" :min="0" :precision="2" class="!w-100%" />
        </el-form-item>
        <el-form-item label="开始" prop="startDate">
          <el-date-picker v-model="form.startDate" type="date" value-format="YYYY-MM-DD" class="!w-100%" />
        </el-form-item>
        <el-form-item label="结束" prop="endDate">
          <el-date-picker v-model="form.endDate" type="date" value-format="YYYY-MM-DD" class="!w-100%" />
        </el-form-item>
        <el-form-item label="主办律师">
          <el-select v-model="form.leadLawyerId" clearable filterable class="!w-100%">
            <el-option
              v-for="l in lawyers"
              :key="l.id"
              :label="`${l.name || '律师'}（#${l.id}）`"
              :value="l.id"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submitCreate">保存</el-button>
      </template>
    </Dialog>

    <Dialog v-model="logVisible" title="服务记录" width="640px">
      <el-form :inline="true" class="mb-12px">
        <el-form-item label="日期">
          <el-date-picker v-model="logForm.serviceDate" type="date" value-format="YYYY-MM-DD" />
        </el-form-item>
        <el-form-item label="内容">
          <el-input v-model="logForm.content" class="!w-200px" />
        </el-form-item>
        <el-form-item label="小时">
          <el-input-number v-model="logForm.hours" :min="0" :precision="1" :step="0.5" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="logSaving" @click="addLog">添加</el-button>
        </el-form-item>
      </el-form>
      <el-table :data="logs" border>
        <el-table-column prop="serviceDate" label="日期" width="110" />
        <el-table-column prop="content" label="内容" min-width="180" show-overflow-tooltip />
        <el-table-column prop="hours" label="小时" width="80" />
      </el-table>
    </Dialog>
  </ContentWrap>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import type { FormInstance, FormRules } from 'element-plus'
import {
  FirmOfficeApi,
  type PracticeRetainerLogVO,
  type PracticeRetainerVO
} from '@/api/firm/office'
import { FirmPracticeApi, type PracticeCustomerVO, type PracticeMatterVO } from '@/api/firm/practice'
import { FirmPortalApi, type FirmLawyerVO } from '@/api/firm/portal'

defineOptions({ name: 'FirmPracticeRetainer' })

const message = useMessage()
const loading = ref(false)
const list = ref<PracticeRetainerVO[]>([])
const total = ref(0)
const query = reactive({
  pageNo: 1,
  pageSize: 10,
  status: undefined as string | undefined
})

const customers = ref<PracticeCustomerVO[]>([])
const matters = ref<PracticeMatterVO[]>([])
const lawyers = ref<FirmLawyerVO[]>([])
const createVisible = ref(false)
const saving = ref(false)
const formRef = ref<FormInstance>()
const form = reactive({
  customerId: undefined as number | undefined,
  caseId: undefined as number | undefined,
  title: '',
  annualFee: 0,
  startDate: '',
  endDate: '',
  leadLawyerId: undefined as number | undefined
})
const rules: FormRules = {
  customerId: [{ required: true, message: '请选择客户', trigger: 'change' }],
  title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
  annualFee: [{ required: true, message: '请输入年费', trigger: 'blur' }],
  startDate: [{ required: true, message: '请选择开始日', trigger: 'change' }],
  endDate: [{ required: true, message: '请选择结束日', trigger: 'change' }]
}

const logVisible = ref(false)
const logSaving = ref(false)
const currentRetainerId = ref<number>()
const logs = ref<PracticeRetainerLogVO[]>([])
const logForm = reactive({ serviceDate: '', content: '', hours: undefined as number | undefined })

const getList = async () => {
  loading.value = true
  try {
    const data = await FirmOfficeApi.retainerPage(query)
    list.value = data?.list || []
    total.value = data?.total || 0
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  query.pageNo = 1
  getList()
}

const openCreate = async () => {
  Object.assign(form, {
    customerId: undefined,
    caseId: undefined,
    title: '',
    annualFee: 0,
    startDate: '',
    endDate: '',
    leadLawyerId: undefined
  })
  createVisible.value = true
  const [c, m, l] = await Promise.all([
    FirmPracticeApi.customerPage({ pageNo: 1, pageSize: 100 }),
    FirmPracticeApi.matterPage({ pageNo: 1, pageSize: 100 }),
    FirmPortalApi.lawyerAssignableList()
  ])
  customers.value = c?.list || []
  matters.value = m?.list || []
  lawyers.value = l || []
}

const submitCreate = async () => {
  await formRef.value?.validate()
  if (!form.customerId) return
  saving.value = true
  try {
    await FirmOfficeApi.retainerCreate({
      customerId: form.customerId,
      caseId: form.caseId,
      title: form.title.trim(),
      annualFee: form.annualFee,
      startDate: form.startDate,
      endDate: form.endDate,
      leadLawyerId: form.leadLawyerId
    })
    message.success('已创建')
    createVisible.value = false
    await getList()
  } finally {
    saving.value = false
  }
}

const doTerminate = async (row: PracticeRetainerVO) => {
  if (!row.id) return
  try {
    await message.confirm('确认终止该顾问协议？')
  } catch {
    return
  }
  await FirmOfficeApi.retainerTerminate(row.id)
  message.success('已终止')
  await getList()
}

const openLogs = async (row: PracticeRetainerVO) => {
  if (!row.id) return
  currentRetainerId.value = row.id
  Object.assign(logForm, { serviceDate: '', content: '', hours: undefined })
  logVisible.value = true
  logs.value = (await FirmOfficeApi.retainerServiceLogList(row.id)) || []
}

const addLog = async () => {
  if (!currentRetainerId.value || !logForm.serviceDate || !logForm.content.trim()) {
    message.warning('请填写日期与内容')
    return
  }
  logSaving.value = true
  try {
    await FirmOfficeApi.retainerServiceLogCreate({
      retainerId: currentRetainerId.value,
      serviceDate: logForm.serviceDate,
      content: logForm.content.trim(),
      hours: logForm.hours
    })
    message.success('已添加')
    Object.assign(logForm, { serviceDate: '', content: '', hours: undefined })
    logs.value = (await FirmOfficeApi.retainerServiceLogList(currentRetainerId.value)) || []
  } finally {
    logSaving.value = false
  }
}

onMounted(getList)
</script>
